import { calloutSurface, codeSurface } from './colorBlending';
import {
  groupRectsIntoLines,
  pillMinimapColor,
  textLineHeight,
  textLineLayout,
  variableSegmentsForLines,
} from './domUtils';
import { snapshotDocTable } from './tableMinimapSnapshot';

/**
 * Build a content node from a block wrapper. `leftFrac`/`widthFrac` are
 * fractions of the document column so horizontal layout survives the
 * abstract vertical scale. Returns null for skippable blocks.
 */
// Per-wrapper measurement cache: the Range.getClientRects walk (text blocks)
// and the line-frac computation (code blocks) are the expensive parts of a
// measure, and between two measures almost every block is unchanged. Keyed by
// the live DOM element (auto-GCed on unmount); validated by size + content.
const fracsCache = new WeakMap();

const cachedFracs = (el, width, height, contentKey, compute) => {
  const hit = fracsCache.get(el);
  if (hit && hit.width === width && hit.height === height && hit.contentKey === contentKey) {
    return hit.fracs;
  }
  const fracs = compute();
  fracsCache.set(el, { width, height, contentKey, fracs });
  return fracs;
};

// Canvas used to measure monospace runs with the textarea's real font
let codeMeasure = { font: null, ctx: null };
const codeMeasureCtx = (style) => {
  const font = `${style.fontWeight || '400'} ${style.fontSize} ${style.fontFamily}`;
  if (codeMeasure.font === font && codeMeasure.ctx) return codeMeasure.ctx;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  codeMeasure = { font, ctx };
  return ctx;
};

/**
 * One source line → one or more glyph runs (non-whitespace spans).
 * Critical for ASCII/trees: "path/          description" must show as TWO
 * bars with a gap — a single bar-per-line looks like uniform placeholders
 * when every line is roughly the same total width.
 *
 * Scale is the VISIBLE text column (scrollport), matching on-screen proportions.
 * Scaling to the longest line / max-content width crushed short lines whenever
 * any line (or the unwrapped editable) was wider than the panel.
 * Returns: Array<Array<{ start, width }>>  (runs per source line)
 */
const codeLineSegments = (value, style, contentWidthPx) => {
  const ctx = codeMeasureCtx(style);
  const expandTabs = (s) => s.replace(/\t/g, '    ');
  const lines = String(value ?? '').split('\n').map(expandTabs);
  const fullW = Math.max(40, contentWidthPx);

  const lineRuns = lines.map((line) => {
    const runs = [];
    let i = 0;
    while (i < line.length) {
      while (i < line.length && line[i] === ' ') i += 1;
      if (i >= line.length) break;
      const from = i;
      while (i < line.length && line[i] !== ' ') i += 1;
      const startPx = ctx.measureText(line.slice(0, from)).width;
      const endPx = ctx.measureText(line.slice(0, i)).width;
      // Clamp to the visible column — overflow reads as full-width in the mini
      if (startPx >= fullW) break;
      const start = Math.min(0.99, startPx / fullW);
      const width = Math.max(
        1 / fullW,
        Math.min(1 - start, (Math.min(endPx, fullW) - startPx) / fullW)
      );
      runs.push({ start, width });
    }
    return runs; // empty → blank source line
  });

  return lineRuns.length ? lineRuns : [[]];
};

export const buildNode = (wrapper, blocksRect, baseTop) => {
  const br = wrapper.getBoundingClientRect();
  if (br.height <= 0 || br.width <= 0) return null;

  const base = {
    id: wrapper.getAttribute('data-block-id'),
    docTop: br.top - baseTop,
    docBottom: br.bottom - baseTop,
    leftFrac: (br.left - blocksRect.left) / blocksRect.width,
    widthFrac: br.width / blocksRect.width,
  };

  // Layout containers — descend
  if (wrapper.classList.contains('row-block-wrapper')) {
    const columns = [];
    wrapper.querySelectorAll(':scope > .row-block > [data-block-id]').forEach((colEl) => {
      const colRect = colEl.getBoundingClientRect();
      if (colRect.width <= 0) return;
      const children = [];
      colEl.querySelectorAll(':scope > .column-block > [data-block-id]').forEach((child) => {
        const node = buildNode(child, blocksRect, baseTop);
        if (node) children.push(node);
      });
      columns.push({ children });
    });
    if (columns.length === 0) return null;
    return { ...base, variant: 'row', columns };
  }

  const choice = wrapper.querySelector(':scope > .choice-block');
  if (choice) {
    const children = [];
    wrapper
      .querySelectorAll(':scope > .choice-block > .choice-body .choice-path--active > [data-block-id]')
      .forEach((child) => {
        const node = buildNode(child, blocksRect, baseTop);
        if (node) children.push(node);
      });
    return { ...base, variant: 'choice', showAll: choice.classList.contains('choice-block--all'), children };
  }

  const callout = wrapper.querySelector(':scope > .callout-block');
  if (callout) {
    const children = [];
    wrapper
      .querySelectorAll(':scope > .callout-block > .callout-content > [data-block-id]')
      .forEach((child) => {
        const node = buildNode(child, blocksRect, baseTop);
        if (node) children.push(node);
      });
    return { ...base, variant: 'callout', surface: calloutSurface(callout), children };
  }

  // Toggle: a header line plus (when open) its children, indented like the
  // page. Closed toggles contribute only the header — children are unmounted.
  const toggle = wrapper.querySelector(':scope > .toggle-block');
  if (toggle) {
    const open = toggle.classList.contains('open');
    const children = [];
    wrapper
      .querySelectorAll(':scope > .toggle-block > .toggle-content > [data-block-id]')
      .forEach((child) => {
        const node = buildNode(child, blocksRect, baseTop);
        if (node) children.push(node);
      });
    const title = toggle.querySelector(':scope > .toggle-header .toggle-title');
    let lineVars = null;
    if (title?.querySelector?.('[data-doc-variable], [data-doc-math]')) {
      const layout = cachedFracs(title, br.width, br.height, title.innerHTML, () => {
        return textLineLayout(title.closest('.block-wrapper') || wrapper, br.width);
      });
      lineVars = layout?.lineVars || null;
    }
    return { ...base, variant: 'toggle', open, children, lineVars };
  }

  // Code block: dark panel + per-line glyph runs (gaps preserved)
  const codeBlock = wrapper.querySelector(':scope > .code-block');
  const codeBody = codeBlock?.querySelector(
    '.code-block-editable, .code-block-textarea'
  );
  if (codeBody) {
    const value =
      codeBody.tagName === 'TEXTAREA' ? codeBody.value || '' : codeBody.innerText || '';
    // Prefer innerHTML so pill colour / state changes bust the cache (innerText
    // alone wouldn't see --var-color or data-var-state updates).
    const contentKey =
      codeBody.tagName === 'TEXTAREA' ? value : codeBody.innerHTML || value;
    const measured = cachedFracs(codeBody, br.width, br.height, contentKey, () => {
      const style = getComputedStyle(codeBody);
      const padL = parseFloat(style.paddingLeft) || 0;
      const padR = parseFloat(style.paddingRight) || 0;
      const padT = parseFloat(style.paddingTop) || 0;
      const grow = codeBlock.querySelector('.code-block-grow');
      // Visible scrollport only — never codeBody.clientWidth: with wrap off the
      // editable is width:max-content and would inflate the scale (tiny bars).
      const portW =
        grow?.clientWidth ||
        codeBlock.querySelector('.code-block-body')?.clientWidth ||
        br.width;
      const innerW = Math.max(40, portW - padL - padR);
      const sourceRuns = codeLineSegments(value, style, innerW);
      const lh = parseFloat(style.lineHeight);
      const lineH = Number.isFinite(lh) && lh > 0 ? lh : 18;
      const headerH =
        codeBlock.querySelector('.code-block-header')?.getBoundingClientRect()
          .height || 0;
      const headerLines = Math.max(1, Math.round(headerH / lineH));

      // Visual lines (wrap-aware) via getClientRects — must drive BOTH layout
      // height and paint, or wrapped code leaves huge empty gaps in the mini.
      let visualLines = [];
      try {
        const range = document.createRange();
        range.selectNodeContents(codeBody);
        const rects = Array.from(range.getClientRects()).filter(
          (r) => r.width > 0 && r.height > 0
        );
        range.detach?.();
        visualLines = groupRectsIntoLines(rects, lineH);
      } catch {
        visualLines = [];
      }
      const bodyRect = codeBody.getBoundingClientRect();
      const originLeft = bodyRect.left + padL;
      const visualRuns =
        visualLines.length > 0
          ? visualLines.map((l) => {
              const start = Math.max(
                0,
                Math.min(0.99, (l.left - originLeft) / innerW)
              );
              const width = Math.max(
                1 / innerW,
                Math.min(1 - start, (l.right - l.left) / innerW)
              );
              return [{ start, width }];
            })
          : null;

      // Prefer source glyph-runs (ASCII gaps) when wrap hasn't multiplied lines;
      // otherwise paint every visual line so the mini matches on-screen density.
      const wrapped =
        visualRuns && visualRuns.length > sourceRuns.length + 1;
      const lineFracs = wrapped ? visualRuns : sourceRuns;
      const layoutLines = headerLines + lineFracs.length;

      let lineVars = null;
      if (codeBody.tagName !== 'TEXTAREA' && codeBody.querySelector('[data-doc-variable], [data-doc-math]')) {
        const linesForVars =
          visualLines.length > 0
            ? visualLines
            : lineFracs.map((_, i) => ({
                top: bodyRect.top + padT + i * lineH,
                left: 0,
                right: 0,
              }));
        lineVars = variableSegmentsForLines(
          codeBody,
          linesForVars,
          innerW,
          originLeft,
          lineH
        );
      }
      return { lineFracs, lineVars, layoutLines, headerLines };
    });
    return {
      ...base,
      variant: 'code',
      lines: measured.layoutLines,
      headerLines: measured.headerLines,
      lineFracs: measured.lineFracs,
      lineVars: measured.lineVars,
      surface: codeSurface(codeBlock),
      docHeight: br.height,
    };
  }

  // NOTE: exact classes only — a substring match like [class*="table"] would
  // catch every text block via "doc-ediTABLE"
  const tableRoot = wrapper.querySelector(
    ':scope > .table-block-wrapper, :scope > .table-block'
  );
  if (tableRoot) {
    // innerHTML so pill colour / upload thumbs bust the cache
    const contentKey = tableRoot.innerHTML || tableRoot.innerText || '';
    const snapshot = cachedFracs(tableRoot, br.width, br.height, contentKey, () =>
      snapshotDocTable(tableRoot, Math.max(1, br.width), pillMinimapColor)
    );
    const lines =
      1 /* header */ +
      (snapshot.empty ? 1 : snapshot.rows.length) +
      (snapshot.hasAdd ? 1 : 0);
    return {
      ...base,
      variant: 'table',
      lines,
      colFracs: snapshot.colFracs,
      header: snapshot.header,
      rows: snapshot.rows,
      empty: snapshot.empty,
      hasAdd: snapshot.hasAdd,
      docHeight: br.height,
    };
  }

  // In-doc variables table — miniature of the real chrome + grid
  // (label, header, color dots, name/value/description/scope columns).
  const variablesRoot = wrapper.querySelector(':scope > .variables-block');
  if (variablesRoot) {
    const contentKey = variablesRoot.innerText || '';
    const snapshot = cachedFracs(variablesRoot, br.width, br.height, contentKey, () => {
      const rootW = Math.max(1, br.width);
      const fieldText = (el) => {
        if (!el) return '';
        if (typeof el.value === 'string') return el.value;
        return (el.textContent || '').replace(/\s+/g, ' ').trim();
      };
      const cellFrac = (text, colPx) => {
        const t = (text || '').replace(/\s+/g, ' ').trim();
        if (!t) return 0.14;
        const budget = Math.max(3, Math.floor(colPx / 7));
        return Math.min(1, Math.max(0.2, t.length / budget));
      };

      // Live column widths from the header (or first body row) — color plus
      // the five content tracks (name / type / value / description / scope).
      const colSample =
        variablesRoot.querySelector('.variables-panel__table-header') ||
        variablesRoot.querySelector('.variables-panel__table-row');
      const isVarsChrome = (el) =>
        /--drag|--color|--actions/.test(el.className || '');
      let colFracs = null;
      if (colSample) {
        const colorEl = colSample.querySelector(
          '.variables-panel__col--color, .variables-panel__cell--color'
        );
        const contentEls = Array.from(colSample.children).filter((el) => !isVarsChrome(el));
        const measured = [colorEl, ...contentEls].filter(Boolean);
        if (measured.length >= 5) {
          const widths = measured.map((el) => el.getBoundingClientRect().width);
          const sum = widths.reduce((a, b) => a + b, 0);
          if (sum > 0) {
            colFracs = widths.map((w) => w / rootW);
          }
        }
      }
      if (!colFracs) {
        colFracs = [0.05, 0.22, 0.1, 0.24, 0.2, 0.16];
      }

      const header = {
        cells: [0.55, 0.35, 0.45, 0.5, 0.4],
      };

      const rows = Array.from(
        variablesRoot.querySelectorAll('.variables-panel__table-row')
      ).map((row) => {
        const nameEl = row.querySelector('.variables-panel__input--name');
        const typeEl = row.querySelector(
          '.variables-panel__type-label, .variables-panel__type-select'
        );
        const valueEl = row.querySelector('.variables-panel__input--value');
        const descEl = row.querySelector('.variables-panel__input--description');
        const scopeEl = row.querySelector(
          '.variables-panel__scope-label, .variables-panel__scope-select'
        );
        const dot = row.querySelector('.variables-panel__color-dot');
        const nameW = (colFracs[1] || 0.22) * rootW;
        const typeW = (colFracs[2] || 0.1) * rootW;
        const valueW = (colFracs[3] || 0.24) * rootW;
        const descW = (colFracs[4] || 0.2) * rootW;
        const scopeW = (colFracs[5] || 0.16) * rootW;
        return {
          readonly: row.classList.contains('variables-panel__table-row--readonly'),
          color: dot ? getComputedStyle(dot).backgroundColor : null,
          cells: [
            cellFrac(fieldText(nameEl), nameW),
            cellFrac(fieldText(typeEl), typeW),
            cellFrac(fieldText(valueEl), valueW),
            cellFrac(fieldText(descEl), descW),
            cellFrac(fieldText(scopeEl), scopeW),
          ],
        };
      });

      return {
        colFracs,
        header,
        rows,
        empty: rows.length === 0,
        hasAdd: !!variablesRoot.querySelector('.variables-panel__add-btn'),
      };
    });

    // label + header + body rows (+ empty stub / add) — one mini line each
    const lines =
      1 /* label */ +
      1 /* header */ +
      (snapshot.empty ? 1 : snapshot.rows.length) +
      (snapshot.hasAdd ? 1 : 0);

    return {
      ...base,
      variant: 'variables',
      lines,
      colFracs: snapshot.colFracs,
      header: snapshot.header,
      rows: snapshot.rows,
      empty: snapshot.empty,
      hasAdd: snapshot.hasAdd,
      docHeight: br.height,
    };
  }

  if (wrapper.querySelector(':scope > .heading-block, :scope h1, :scope h2, :scope h3, :scope h4')) {
    const editable = wrapper.querySelector('[contenteditable], .doc-editable') || wrapper;
    const layout = cachedFracs(editable, br.width, br.height, editable.innerHTML, () => {
      return (
        textLineLayout(wrapper, br.width) || {
          lineFracs: [0.45],
          lineVars: [[]],
        }
      );
    });
    return {
      ...base,
      variant: 'heading',
      lineFracs: layout.lineFracs,
      lineVars: layout.lineVars,
    };
  }
  if (wrapper.querySelector(':scope > .divider-block, :scope hr')) {
    return { ...base, variant: 'divider' };
  }
  if (
    wrapper.querySelector(
      ':scope img, :scope video, :scope > .video-block .video-block__frame'
    )
  ) {
    const img = wrapper.querySelector(':scope img');
    const video = wrapper.querySelector(':scope video');
    const mediaEl =
      wrapper.querySelector(':scope > .image-block .image-block__frame') ||
      wrapper.querySelector(':scope > .video-block .video-block__frame') ||
      img ||
      video;
    const mr = mediaEl?.getBoundingClientRect?.();
    const src = img ? img.currentSrc || img.src || null : null;
    if (mr && mr.width > 0 && mr.height > 0) {
      return {
        ...base,
        leftFrac: (mr.left - blocksRect.left) / blocksRect.width,
        widthFrac: mr.width / blocksRect.width,
        variant: 'media',
        docHeight: mr.height,
        src,
      };
    }
    return { ...base, variant: 'media', docHeight: br.height, src };
  }

  // Text-ish (paragraphs, bullets, checkboxes). Blank blocks still consume
  // vertical space in the mini doc — an empty line is spacing, not nothing.
  const text = wrapper.textContent.trim();
  if (!text) {
    return { ...base, variant: 'blank' };
  }
  // The Range.getClientRects walk is the most expensive read in a measure —
  // cache it per block so a full remeasure after typing only re-walks the
  // block whose content actually changed. innerHTML as the key: inline
  // formatting (bold, code, pills / --var-color) changes rendered widths too.
  const editable = wrapper.querySelector('[contenteditable], .doc-editable') || wrapper;
  const layout = cachedFracs(editable, br.width, br.height, editable.innerHTML, () => {
    return (
      textLineLayout(wrapper, br.width) || {
        lineFracs: [
          ...new Array(Math.max(1, Math.round(br.height / textLineHeight(wrapper)))).fill(1),
        ],
        lineVars: null,
      }
    );
  });
  const checkboxToggle = wrapper.querySelector(':scope > .checkbox-block .checkbox-toggle');
  if (checkboxToggle) {
    return {
      ...base,
      variant: 'checkbox',
      checked: checkboxToggle.getAttribute('aria-checked') === 'true',
      indeterminate: checkboxToggle.getAttribute('aria-checked') === 'mixed',
      lines: layout.lineFracs.length,
      lineFracs: layout.lineFracs,
      lineVars: layout.lineVars,
    };
  }
  return {
    ...base,
    variant: 'text',
    lines: layout.lineFracs.length,
    lineFracs: layout.lineFracs,
    lineVars: layout.lineVars,
  };
};
